// frontend/src/pages/Country/CountryList.jsx
import React, { useEffect, useState } from "react";
import { Edit, Trash2, Plus, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import "./CountryList.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CountryList = () => {
  const { user } = useAuth();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/country`, {
        headers: getAuthHeaders(),
      });

      setCountries(res.data.data || []);
    } catch (error) {
      toast.error("Failed to fetch countries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this country?")) {
      try {
        await axios.delete(`${url}/api/country/${id}`, {
          headers: getAuthHeaders(),
        });
        toast.success("Country deleted successfully");
        fetchCountries();
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete country");
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Loading countries...</div>
      </div>
    );
  }

  return (
    <div className="country-container">
      <div className="header">
        <div>
          <h2>Country List</h2>
          <p className="info">
            Showing all countries in the system
          </p>
        </div>
        <button onClick={() => navigate("/AddCountry")} className="add-button">
          <Plus size={16} /> Add Country
        </button>
      </div>

      <div className="stats-bar">
        <span>Total: {countries.length} countries</span>
        <span>Created by: {user?.name || "You"}</span>
      </div>

      <table className="country-table">
        <thead>
          <tr>
            <th>Country Name</th>
            <th>Created By</th>
            <th>Created At</th>
            <th>Last Updated</th>
            <th>Status</th>
            <th style={{ textAlign: "center" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {countries.length === 0 ? (
            <tr>
              <td colSpan="6" className="no-data">
                No countries found. Create your first country!
              </td>
            </tr>
          ) : (
            countries.map((country) => (
              <tr key={country._id}>
                <td>
                  <div className="country-name">
                    <strong>{country.name}</strong>
                  </div>
                </td>
                <td>
                  <div className="user-info">
                    <User size={12} />
                    <span>
                      {country.created_by?.name ||
                       country.created_by?.email ||
                       "System"}
                    </span>
                  </div>
                </td>
                <td>
                  {new Date(country.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </td>
                <td>
                  {country.updated_at
                    ? new Date(country.updated_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : "Never"
                  }
                </td>
                <td>
                  <span className={`status-badge ${country.status}`}>
                    {country.status}
                  </span>
                </td>
                <td className="actions">
                  <button
                    onClick={() => navigate(`/country/edit/${country._id}`)}
                    className="edit-btn"
                    title="Edit"
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(country._id)}
                    className="delete-btn"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CountryList;